/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Paper } from "@material-ui/core";
import { UserList } from "./UserList";
import { UserDetails } from "./UserDetails";
import { updatePaceUsers, getAllUsers, getAllRoles } from "../../services/UserService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { SyncUsersDialogue } from "./SyncUsersDialogue";
import { DEFAULT_ADMIN_ROLE, DEFAULT_AGENT_ROLE } from "../../../../utility/permissions";
import useRemoteConfig from "../../../../hooks/useRemoteConfig";
import {
	ActionButtons,
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
} from "../../../../components/Common";
const COMPONENT_NAME = "Admin SettingsManager/pages/UserManagementPage";

const useStyles = makeStyles({
	actionButtons: {
		maxHeight: "unset",
	},
});

/**
 * This page is used for editing PACE users.
 * This page deals with syncing users to PACE and editing them and their permissions.
 */
export const UserLayout = ({ showSnackbar, setTabModified }) => {
	const [loadedUsersObject, setLoadedUsersObject] = React.useState(undefined);
	const [localUsersObject, setLocalUsersObject] = React.useState(undefined);
	const [editedUsersObject, setEditedUsersObject] = useState({});
	const [userUpdateObject, setUserUpdateObject] = React.useState(undefined);
	const [selectedUser, setSelectedUser] = useState(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [sync, setSync] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [save, setSave] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loadPage, setLoadPage] = useState(true);
	const [edited, setEdited] = useState(false);
	const [roleTypes, setRoleTypes] = useState({});
	const { config } = useRemoteConfig(["TeamsSyncConfiguration", "TEAMS_USER_STATUS_SYNC_STATE"]);
	const classes = useStyles();

	const isTeamsSyncPipelineEnabled = config?.TEAMS_USER_STATUS_SYNC_STATE === "ENABLED";
	const isIntegrationsTeamsSyncEnabled = config?.TeamsSyncConfiguration?.TeamsSyncEnabled;
	const isPaceToTeamsSyncEnabled = config?.TeamsSyncConfiguration?.connectToTeamsSyncEnabled;

	const addUserChanges = (userId, fieldName, newValue) => {
		setTabModified(true);
		const newEditedUsers = { ...editedUsersObject };
		newEditedUsers[userId] = newEditedUsers[userId] || { userId, savedUser: loadedUsersObject[userId] };
		if (fieldName !== "edited" && (fieldName !== "email" || (newValue && newValue.length > 0)))
			newEditedUsers[userId][fieldName] = newValue;
		const newLocalUsers = { ...localUsersObject };
		newLocalUsers[userId] = { ...newLocalUsers[userId], ...newEditedUsers[userId], edited: true };
		setEdited(Object.keys(newEditedUsers).length > 0);
		setEditedUsersObject(newEditedUsers);
		setLocalUsersObject(newLocalUsers);
	};

	const removeUserChanges = (userId) => {
		const newEditedUsers = { ...editedUsersObject };
		delete newEditedUsers[userId];
		const newLocalUsers = { ...localUsersObject };
		newLocalUsers[userId] = { ...loadedUsersObject[userId], newUser: false };
		const hasEditedChanges = Object.keys(newEditedUsers).length > 0;
		setEditedUsersObject(newEditedUsers);
		setEdited(hasEditedChanges);
		setLocalUsersObject(newLocalUsers);
		setSelectedUser({ ...loadedUsersObject[userId] });
		setTabModified(hasEditedChanges);
	};
	const fetchUsers = useCallback(
		async (syncUsers) => {
			try {
				const result = await getAllUsers();
				const { paceUsers, newUsers, oldUsers } = result;
				const serverRoles = await getAllRoles();
				const results = [...serverRoles, DEFAULT_ADMIN_ROLE, DEFAULT_AGENT_ROLE];
				const roles = {};
				results.forEach((role) => {
					roles[role.roleId] = role;
				});
				setRoleTypes(roles);
				setLoadedUsersObject(paceUsers);
				setLocalUsersObject(paceUsers);
				if (newUsers.newConnectUsers || newUsers.newCognitoUsers || oldUsers.oldConnectUsers) {
					setUserUpdateObject({ newUsers, oldUsers });
				} else if (syncUsers) {
					showSnackbar("No user updates found.", "info");
				}
			} catch (error) {
				v2.error(`Error while fetching users`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_USERROLE_LIST_API_ERROR,
				});
				showSnackbar("Failed to load users.", "error");
			} finally {
				setIsLoading(false);
				setSyncing(false);
			}
		},
		[showSnackbar],
	);

	const saveUsers = useCallback(
		async (editedUsersObject, setSaving, localUsersObject) => {
			try {
				await updatePaceUsers(editedUsersObject);
			} catch (error) {
				v2.error(`Error while saving user`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_USERROLE_ERROR,
				});
			} finally {
				setSaving(false);
				setEdited(false);
				const newUsersObject = { ...localUsersObject };
				Object.keys(editedUsersObject).forEach((key) => delete newUsersObject[key].edited);
				setLoadedUsersObject(newUsersObject);
				setLocalUsersObject(newUsersObject);
				setSelectedUser(undefined);
				setEditedUsersObject({});
				showSnackbar("Successfully saved users.", "success");
				setTabModified(false);
			}
		},
		[showSnackbar, setTabModified],
	);

	useEffect(() => {
		if (save && !saving) {
			setSaving(true);
			setSave(false);
			saveUsers(editedUsersObject, setSaving, localUsersObject);
		}
	}, [save, saving, editedUsersObject, saveUsers, localUsersObject]);

	useEffect(() => {
		if (loadPage) {
			setLoadPage(false);
			fetchUsers(false);
		}
	}, [fetchUsers, loadPage]);

	useEffect(() => {
		if (sync && !syncing) {
			setSync(false);
			setSyncing(true);
			fetchUsers(true);
		}
	}, [sync, syncing, fetchUsers]);

	useEffect(() => {
		if (selectedUser) {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth",
			});
		}
	}, [selectedUser]);

	const renderSyncUserDialog = userUpdateObject && (
		<SyncUsersDialogue
			usersToProcess={userUpdateObject}
			finishSyncing={(newUsers, deletedUsers) => {
				setUserUpdateObject(undefined);
				setSyncing(false);
				let newUserObject = { ...loadedUsersObject };
				if (newUsers) {
					newUserObject = { ...newUserObject, ...newUsers };
				}
				if (deletedUsers) {
					Object.keys(deletedUsers).forEach((key) => {
						if (newUserObject[key]) delete newUserObject[key];
					});
				}
				if (newUsers || deletedUsers) {
					showSnackbar("All users synced.", "success");
					setSelectedUser(undefined);
				}
				setLoadedUsersObject({ ...newUserObject });
				setLocalUsersObject({ ...newUserObject });
			}}
		/>
	);

	const onCancel = () => {
		const newLocalUsers = { ...loadedUsersObject };
		Object.keys(newLocalUsers).forEach((key) => (newLocalUsers[key].newUser = localUsersObject[key].newUser));
		setLocalUsersObject(newLocalUsers);
		setEdited(false);
		setTabModified(false);
		setSelectedUser({ ...loadedUsersObject[selectedUser.userId] });
		setEditedUsersObject({});
	};

	const onSave = () => {
		setSave(true);
	};

	const newActionButtons = (
		<ActionButtons
			className={`d-flex justify-content-between flex-wrap-reverse ${edited && "mt-5"} mt-lg-0 ${
				classes.actionButtons
			}`}
			saveBtnClass="col-12 col-xl-5 float-right"
			cancelBtnClass="col-12 col-xl-5 float-left"
			showCancel={edited}
			showSave={edited}
			cancelLabel={"Cancel All"}
			saveLabel={"Save All"}
			onCancel={onCancel}
			onSave={onSave}
		/>
	);

	return (
		<ContentLayout style={{ marginTop: 0 }}>
			{renderSyncUserDialog}
			{(isLoading || syncing || saving) && <ProgressOverlay />}
			<PanelContainer>
				<LeftPanelLayout>
					<Paper style={{ minHeight: 600, padding: "10px 5px" }} className="d-flex flex-column w-100">
						<UserList
							usersObject={localUsersObject}
							newActionButtons={newActionButtons}
							selectUserHandler={(user) => {
								setSelectedUser(user);
								const newLocalUsers = { ...localUsersObject };
								newLocalUsers[user.userId] = { ...user, newUser: false };
								setLocalUsersObject({ ...newLocalUsers });
							}}
							selectedUser={selectedUser}
							roleTypes={roleTypes}
							onSyncClick={() => setSync(true)}
						/>
					</Paper>
				</LeftPanelLayout>
				<RightPanelLayout>
					<Paper>
						{!loadedUsersObject && !isLoading && (
							<div className="mt-4">
								<span className="text-danger mr-2">Failed to load Users, please try again.</span>
							</div>
						)}
						<UserDetails
							isTeamsSyncPipelineEnabled={isTeamsSyncPipelineEnabled}
							isIntegrationsTeamsSyncEnabled={isIntegrationsTeamsSyncEnabled}
							isPaceToTeamsSyncEnabled={isPaceToTeamsSyncEnabled}
							setEdited={setEdited}
							addUserChanges={addUserChanges}
							removeUserChanges={removeUserChanges}
							user={selectedUser}
							roleTypes={roleTypes}
						/>
					</Paper>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};
